import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import insuranceSquare from './../../assets/images/insurance-shapes-square.svg';
import insuranceTriangle from './../../assets/images/insurance-shapes-triangle.svg';
import insuranceMetricsChart from './../../assets/images/insurance-mail-chart.png';
import '../insurance.scss';
import { gsap } from 'gsap';
import AOS from "aos";
import "aos/dist/aos.css";

export default function InsuranceMetrics() {

  const square1Ref = useRef();
  const square2Ref = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        //console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        //console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        //console.log(bar);
        barArray.push(bar);
      }

      //console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');

          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }
        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        //console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        //console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      //console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"})
        .to(square1Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(square2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => { ctx.revert(); /* <- cleanup! */ };


  }, []);

  useEffect(() => {
    AOS.init({
      once: true
    });
    AOS.refresh();
  }, []);

  return (
    <>
      <div className="insurance-metrics">

        <p id="s1" className="intro" data-aos="fade-up">Explore the data to see how insurance companies track and measure the success of their direct mail.&#8288;<a href="#reference-1" className="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-right'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Checking Mail Effectiveness</h3>
                <div className='pie-chart' data-aos>
                  <svg viewBox="0 0 32 32" role="img" alt="" title="">
                    <circle r="16" cx="16" cy="16" strokeDasharray="0 100"></circle>
                  </svg>
                </div>

                <span data-aos data-aos-id="percentage" data-aos-anchor=".pie-chart">
                  <span data-aos className='percentage-value'><span class="percentage" data-to="91" aria-hidden="true">0</span><span class="screen-reader-text">91</span>%</span>
                </span>

                <p>Always or most of the time</p>
                <p>The vast majority of insurance companies check the effectiveness of their direct mail campaigns always or most of the time.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p><strong><a href="/../track-direct-mail-roi/">Post-purchase surveys, business reply cards, promo codes, coupons and more</a></strong> can all make it easier to track the success of direct mail while gathering valuable data to help inform future campaigns.</p>
              </div>
            </div> 
          </div>

          <div className='col-left'>
            
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Direct Mail Response Rates</h3>
                
                <div className='progress-bar-area' data-aos data-aos-id="bar-anim">
                  <div className='progressbar-item'>
                    <span className='label'>Less than 5%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="5"><span class="percentage" data-to="5" aria-hidden="true">0</span><span class="screen-reader-text">5</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>From 6% to 10%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="15"><span class="percentage" data-to="15" aria-hidden="true">0</span><span class="screen-reader-text">15</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>From 11% to 15%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress' data-fill="30"><span class="percentage" data-to="30" aria-hidden="true">0</span><span class="screen-reader-text">30</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>From 16% to 20%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="21"><span class="percentage" data-to="21" aria-hidden="true">0</span><span class="screen-reader-text">21</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>From 21% to 25%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="18"><span class="percentage" data-to="18" aria-hidden="true">0</span><span class="screen-reader-text">18</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>More than 25%</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="10"><span class="percentage" data-to="10" aria-hidden="true">0</span><span class="screen-reader-text">10</span>%</div>
                    </div>
                  </div>
                </div>
                <p>Most insurance businesses report relatively high direct mail response rates, with 30% of companies reporting rates of 11%–15%, followed by 21% reporting rates of 16%–20%.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p><strong><a href="/../best-practices-for-automated-direct-mail-campaigns/">Retargeted direct mail</a></strong> can help increase the chances of customer engagement. By triggering custom mail to be sent out within a couple of days of a customer interacting with one of your digital channels, you can encourage customers to act while keeping your brand top of mind.</p>
              </div>
            </div>
          </div>
        </div>
        <div className='two-cols'>
          <div className='col-left w-50'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Measuring Mail Impact</h3>
                <div className='progress-bar' data-aos data-aos-id="bar-anim">
                  <div className='progress' data-fill="66"><span class="percentage" data-to="66" aria-hidden="true">0</span><span class="screen-reader-text">66</span>%</div>
                </div>
                <p>Response rate</p>
                <p>Insurance businesses typically measure the impact of direct mail by tracking response rates, and they use them more than companies in other industries.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>To calculate direct mail response rate, just divide the number of responses or interactions received by the total number of mailpieces sent out.</p>
              </div>
            </div>
          </div>
          <div className='col-left w-50'>
            <div className='personalized-box secondary align-to-text'>
              <h3>Insurance companies usually track mail impact via response rates.</h3>
              <div className='shape-images'>
                <div className='triangle triangle-1'>
                  <img ref={triangle1Ref} src={insuranceTriangle} alt="" />
                </div>
                <div className='square square-1'>
                  <img ref={square1Ref} src={insuranceSquare} alt="" />
                </div>
                <div className='triangle triangle-2'>
                  <img ref={triangle2Ref} src={insuranceTriangle} alt="" />
                </div>
                <div className='square square-2'>
                  <img ref={square2Ref} src={insuranceSquare} alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
